import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AvatarDisplay } from './AvatarDisplay';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAdmin } from '@/hooks/useAdmin';
import { User, LogOut, Settings, Shield, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const UserMenu = () => {
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error('Error signing out');
    } else {
      toast.success('Signed out successfully');
    }
  };

  if (!user) return null;

  const displayName = profile?.username || profile?.display_name || user.email?.split('@')[0] || 'Player';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative h-10 w-10 rounded-full p-0"
          data-tour="profile"
        >
          <AvatarDisplay
            username={displayName}
            avatarConfig={profile?.avatar_config}
            avatarUrl={profile?.avatar_url}
            size="md"
            showAnimation={false}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-card dark:bg-gradient-to-b dark:from-slate-900 dark:via-purple-950/95 dark:to-slate-950 border-border dark:border-primary/30 text-foreground dark:text-slate-200" align="end" forceMount>
        <div className="flex items-center justify-start gap-3 p-3 border-b border-border dark:border-primary/20">
          <AvatarDisplay
            username={displayName}
            avatarConfig={profile?.avatar_config}
            avatarUrl={profile?.avatar_url}
            size="md"
            showAnimation={true}
          />
          <div className="flex flex-col space-y-1 leading-none">
            <p className="font-medium text-foreground dark:text-white">{displayName}</p>
            <p className="text-xs text-primary dark:text-teen-cyan">
              ELO: {profile?.elo_rating || 1200}
            </p>
          </div>
        </div>
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate('/admin')} className="text-foreground dark:text-slate-300 hover:text-foreground dark:hover:text-white hover:bg-accent/20 dark:hover:bg-purple-500/20 focus:bg-accent/20 dark:focus:bg-purple-500/20 focus:text-foreground dark:focus:text-white cursor-pointer">
            <Shield className="mr-2 h-4 w-4 text-accent dark:text-teen-gold" />
            Admin Console
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/currency/history')} className="text-foreground dark:text-slate-300 hover:text-foreground dark:hover:text-white hover:bg-yellow-500/20 dark:hover:bg-yellow-500/20 focus:bg-yellow-500/20 dark:focus:bg-yellow-500/20 focus:text-foreground dark:focus:text-white cursor-pointer">
          <Wallet className="mr-2 h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          Wallet & History
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.location.href = '/profile/avatar'} className="text-foreground dark:text-slate-300 hover:text-foreground dark:hover:text-white hover:bg-primary/20 dark:hover:bg-purple-500/20 focus:bg-primary/20 dark:focus:bg-purple-500/20 focus:text-foreground dark:focus:text-white cursor-pointer">
          <Settings className="mr-2 h-4 w-4 text-primary dark:text-teen-cyan" />
          Profile Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/security/dashboard')} className="text-foreground dark:text-slate-300 hover:text-foreground dark:hover:text-white hover:bg-green-500/20 dark:hover:bg-green-500/20 focus:bg-green-500/20 dark:focus:bg-green-500/20 focus:text-foreground dark:focus:text-white cursor-pointer">
          <Shield className="mr-2 h-4 w-4 text-green-600 dark:text-green-400" />
          Security Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSignOut} className="text-foreground dark:text-slate-300 hover:text-foreground dark:hover:text-white hover:bg-red-500/20 focus:bg-red-500/20 focus:text-foreground dark:focus:text-white cursor-pointer">
          <LogOut className="mr-2 h-4 w-4 text-red-600 dark:text-teen-red" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};